// @ts-ignore
import type { AccountType, FreightService } from '@prisma/client';

import { compiledRegex, tenants } from './tenants';
import { RequestDrawers, TenantInfo } from './tenants/common';

export { tenants } from './tenants';
export { emailColorMap, type RequestDrawers } from './tenants/common';

type RealTenants = keyof typeof tenants;
export type TenantId = RealTenants | 'any' | 'unknown';

// Canonical shape of the per-tenant customer config. Because each tenant is declared with
// `as const`, `getTenantByHost().customer` is a union of tenant-specific literal shapes and can't be
// read field-by-field; resolve it through this helper to get the full optional config type.
export type TenantCustomerConfig = NonNullable<TenantInfo<string>['customer']>;
export type FreightServicesByAccountType = NonNullable<TenantCustomerConfig['freightServices']>;

export const getCustomerConfig = (hostname: string | null = null): TenantCustomerConfig => {
  const tenant = getTenantByHost(hostname);
  const customer = 'customer' in tenant ? tenant.customer : undefined;
  return (customer ?? {}) as unknown as TenantCustomerConfig;
};

// Per-tenant theme token overrides (`radii`, `semantic`, `fonts`, `heading`, `button`, `field`,
// `input`, `label`), keyed exactly like `CustomerThemeSchema` in `@pelicargo/utils`. Typed as a
// loose record because this package sits below `@pelicargo/utils` in the dependency graph; the
// customer app narrows it. Resolve through here rather than indexing the tenant union - see the
// note on `getCustomerConfig` above.
export const getCustomerThemeTokens = (hostname: string | null = null): Record<string, unknown> => {
  const theme = getTenantByHost(hostname)?.customerTheme as { tokens?: Record<string, unknown> } | undefined;

  return theme?.tokens ?? {};
};

// Built-in per-account-type freight services. Reproduces the historical quote-form behavior and is
// used whenever a tenant does not configure a given account type (or `default`).
const ALL_FREIGHT_SERVICES = ['AFR', 'EXW', 'FCA'] as unknown as FreightService[];
const DEFAULT_FREIGHT_SERVICES: FreightServicesByAccountType = {
  IATA: ['AFR'] as FreightService[],
  IAC: ['AFR'] as FreightService[],
  UNCERTIFIED: ['EXW', 'FCA'] as FreightService[],
  default: ALL_FREIGHT_SERVICES,
};

// Resolves the freight services a customer of the given account type may select on the quote form.
// Tenant config wins per account type, then the tenant's `default`, then the built-in fallback above.
export const getFreightServices = (accountType: AccountType | null = null, noThrow = false): FreightService[] => {
  const { accountTypes } = getTenantByHost();
  const { freightServices } = getCustomerConfig();

  const hasAccountType = accountType !== null;
  if (hasAccountType) {
    // Ensure account type is supported in tenant
    if (!accountTypes[accountType]) {
      if (noThrow) {
        return [];
      }
      throw new Error(`Account type ${accountType} is not supported in tenant`);
    }

    return (
      // Prefer defined services for the account type
      freightServices?.[accountType] ??
      // Then the tenant's `default`
      freightServices?.default ??
      // Then the built-in fallbacks for system
      DEFAULT_FREIGHT_SERVICES?.[accountType] ??
      DEFAULT_FREIGHT_SERVICES?.default
    );
  }

  // If no account type is specified, return the tenant's `default` or the built-in fallbacks for system
  return freightServices?.default ?? DEFAULT_FREIGHT_SERVICES?.default;
};

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Each domain entry compiled to [regex, tenant, specificity]. Every entry — plain string or regex
// pattern — matches the exact host or any subdomain of it (dot-boundary suffix, mirroring the
// historical endsWith behavior). Specificity (regex source length) breaks ties in favor of the
// more specific pattern, e.g. `keet.localhost` over `localhost`.
const domainTests = Object.entries(tenants).flatMap(([name, value]: [RealTenants, (typeof tenants)[RealTenants]]) =>
  value.domains.map((v: string | RegExp) => {
    const source = typeof v === 'string' ? escapeRegExp(v) : v.source;
    const re = new RegExp(`(^|\\.)${source}$`);
    return [re, name, re.source.length] as const;
  }),
);

export const getTenantByHost = (hostname: string | null = null) => {
  return tenants[getTenantId(hostname)];
};

export const getTenantById = (id: TenantId) => {
  return tenants?.[id as RealTenants];
};

export const getTenantId = (hostname: string | null = null) => {
  // @ts-ignore shared between browser/node
  if (hostname === null) hostname = window?.location?.hostname ?? '';

  const domain = hostname.split(':')[0];

  // Fallback for a special case
  if (domain === 'localhost') return 'pelicargo';
  let bestMatch: RealTenants | undefined;
  let bestSpecificity = 0;
  for (const [re, tenant, specificity] of domainTests) {
    if (specificity > bestSpecificity && re.test(domain)) {
      bestMatch = tenant;
      bestSpecificity = specificity;
    }
  }
  return bestMatch as RealTenants;
};

export const getAsset = (filename: string) => {
  return `/${getTenantId()}/${filename}`;
};

export const themeEmail = (tenant: TenantId, content: string) => {
  if (tenant === 'unknown' || tenant === 'any') return content;
  return compiledRegex.reduce((agg, func) => func(tenant, agg), content);
};

export const pathIsFlaggable = (path: string) => {
  const tenant = getTenantByHost();
  const basePath = path.split('/')[1];

  const flag = tenant?.admin?.cruds?.[basePath];
  const isDefined = flag !== undefined;
  const isVisible = flag === true ? true : flag === false ? false : undefined;

  return { isDefined, isVisible };
};

export const isCrudRouteEnabledForTenant = (path: string) => {
  const tenant = getTenantByHost();
  const { isDefined, isVisible } = pathIsFlaggable(path);

  // if it's not defined in config, assume it's okay to see for system tenants only
  if (!isDefined) return tenant?.isSystemTenant ?? false;

  return isVisible;
};

export const isRequestDrawerEnabledForTenant = (id: RequestDrawers) => {
  const tenant = getTenantByHost();
  return tenant?.admin?.inbox?.requestDrawers?.[id] ?? false;
};
